import type { RegionCollection } from 'api-validations/geojson-schemas'
import { type DataFilter, LATEST_DATA } from 'api-validations/stitch'
import { Enum } from 'utils'
import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'

import type { LegendOverrides } from '../legend-editor'
import type { LayerSelection } from './data-layer-state/core'
import type { StreamPartition } from './data-layer-state/stream-group'
import { DrawMode, DrawScene, DrawStyle } from './draw-tool-state/core'
import type {
  ConfigSession,
  DiagnosticsSession,
  RecordHistory,
  ScoutingSession,
  StreamEditSession
} from './mode-sessions'
import { defaultBrowseFields, type Pane, type PaneScope, type Session } from './session'

/** DataFilter extended with a disabled flag so filters can be preserved in state without affecting rendering. */
export type DataFilterConfig = DataFilter & { disabled?: boolean }

export type LassoSelection = {
  collection: RegionCollection
  selectedArea: { value: number; unit: 'ac' }
}

/**
 * The "disable" state is the separate `lassoActive` flag, not a third value
 * here, so the chosen mode survives a disable/re-enable round-trip (mirrors the
 * layer filters' include/exclude + disabled split).
 */
export enum LassoMode {
  Include = 'include',
  Exclude = 'exclude'
}

/**
 * In-flight editing session for a `pass`-key filter row. Drives both the
 * armed/idle UI state (which row in the editor is being edited) and the
 * shader's optimistic preview channel (working selection ∪ hovered blue gets
 * OR'd onto the existing slot uniforms). Distinct from `filters` — clicks
 * mutate this, Save copies it into `filters`, Cancel discards it.
 *
 * The (streamId, applicationId, layerId) triple is the source of truth for
 * which row is armed; pass it through `filterLayerKey()` to compare against
 * a `DataFilterConfig`.
 */
export type PassEditSession = {
  streamId: string
  applicationId: string
  layerId: string
  /** Mirrors the row's include/exclude — preview entries inherit this. */
  type: 'include' | 'exclude'
  /** Working pass blues; never includes the -1 placeholder sentinel. */
  workingBlues: number[]
  /** Pass blue under the cursor right now, or null. Preview only. */
  hoveredBlue: number | null
}

type DataToolsConfig = {
  lasso: LassoSelection | undefined
  lassoActive: boolean
  lassoMode: LassoMode
  /** True while the user is actively drawing/erasing a lasso polygon. */
  lassoDrawing: boolean
  filters: DataFilterConfig[]
  /**
   * Pass-row editing session, or null when no row is armed. Single-armed by
   * construction (assigning a new session replaces any existing one).
   */
  passEdit: PassEditSession | null
}

type PlanningConfig = {
  fieldId: string | null
}

/**
 * One of a guidance plan's lines singled out on the read-only planning layer,
 * shown alone in place of the plan as saved. Named after the guidance layer it
 * was chosen on, so a focus never applies to another plan's layer.
 */
export type GuidanceLineFocus = {
  planningFieldId: string
  planId: string | null
  primitiveId: string
}

type Zone = {
  id: string
  name: string
  color: string
}

type DrawToolConfig = {
  enabled: boolean
  scene: DrawScene
  interaction: DrawMode
  shape: DrawStyle
  zoneEditing?: {
    zones: Map<string, Zone>
  }
  activeGuidanceLine?: {
    id: string
    /** [longitude, latitude] EPSG:4326 */
    pointA: [number, number]
    /** [longitude, latitude] EPSG:4326 */
    pointB: [number, number]
    /** Radius in feet — only present when the primitive is RadialPivot. */
    radiusFt?: number
  }
  /**
   * The currently selected/highlighted feature id. One field for both the
   * active zone (during ZoneEditing) and the active view-only feature
   * (edge/hole/corner during selection scenes) — only one is visible at a
   * time, so they share this slot. `zones.get(selectedId)` resolves it to
   * a Zone when it's a zone id.
   */
  selectedId: string | null
}

export enum DetailsTab {
  Data = 'data',
  Tools = 'tools'
}

export type MapState = {
  /** Top-level discriminator: pre-field browse vs. in-field. */
  session: Session

  /**
   * Per-pane context we cannot fully derive from the pane's selection. `null`
   * covers two cases: we can derive it from the pane's current selection, or the
   * pane arrived with no prior selection to take it from.
   */
  paneScopes: Record<Pane, PaneScope | null>

  /**
   * The selection to restore when the plan editor is cancelled — captured when
   * an editor is entered from another layer, cleared whenever the main pane
   * moves to a non-editor layer. Deliberately not URL-synced: a deep-linked or
   * refreshed editor has no prior layer.
   */
  planEditorReturnTo: LayerSelection | null

  /** Legend customizations, keyed by (streamId, applicationId, layerId). Survives session transitions. */
  legendOverrides: Map<string, LegendOverrides>

  /** Active mode. Meaningful only in-field, and reset on the way back out. */
  mode: MapMode

  /** Current scrubber position. Resets on stream-identity change. */
  record: number

  /**
   * Back/forward jump history shared by Diagnostics and Config. `null` until
   * a navigable record has been visited; resets on stream-identity change.
   * The "current" record is `MapState.record`; this slot holds only the
   * stacks (see `mode-sessions.ts` for the lifecycle rules).
   */
  recordSelections: RecordHistory | null

  /**
   * The pass groupings the user has built in the picker, per pane. Sparse by
   * intent: a task with no entry just uses the same-day default, so we persist
   * only deliberate overrides.
   */
  streamGroups: Record<Pane, Record<string, StreamPartition | undefined>>

  // Per-mode slices. Single instances (not session-keyed); their lifetime is
  // tied to the active stream — survives mode toggles, resets when the main
  // pane commits a different stream.
  detailsTab: DetailsTab
  planning: PlanningConfig
  diagnosticsSession: DiagnosticsSession | null
  configSession: ConfigSession | null
  scoutingSession: ScoutingSession | null
  /**
   * Active stream trim/split editing session, or null when not editing.
   */
  streamEditSession: StreamEditSession | null
  dataTools: DataToolsConfig
  drawTool: DrawToolConfig
  /** Not URL-synced: a deep link to a guidance layer opens on the plan as saved. */
  guidanceLineFocus: GuidanceLineFocus | null
}

export enum MapMode {
  Details = 'details',
  DataTools = 'data-tools',
  Config = 'config',
  Diagnose = 'diagnose',
  Planning = 'planning',
  Scouting = 'scouting',
  None = 'none',
  /** @deprecated: will be replaced by DataTools */
  Lasso = 'lasso',
  /** @deprecated: will be replaced by DataTools */
  SplitMap = 'split'
}

export const modes = Enum.values(MapMode)

export const defaultState: MapState = {
  session: defaultBrowseFields,
  paneScopes: { main: null, comparison: null },
  planEditorReturnTo: null,
  legendOverrides: new Map(),
  record: LATEST_DATA,
  recordSelections: null,
  mode: MapMode.Details,
  detailsTab: DetailsTab.Data,
  streamGroups: { main: {}, comparison: {} },
  planning: { fieldId: null },
  diagnosticsSession: null,
  configSession: null,
  scoutingSession: null,
  streamEditSession: null,
  dataTools: {
    lasso: undefined,
    lassoActive: false,
    lassoMode: LassoMode.Include,
    lassoDrawing: false,
    filters: [],
    passEdit: null
  },
  drawTool: {
    enabled: false,
    scene: DrawScene.Boundary,
    interaction: DrawMode.Lasso,
    shape: DrawStyle.Freehand,
    selectedId: null
  },
  guidanceLineFocus: null
}
/**
 * @internal Not re-exported from the package barrel — only use within
 * `shared-ui/features/map/map-state-store`. Name must start with `use` so the
 * React compiler recognizes it as a hook (leading underscore breaks that).
 */
export const useMapStateStore = create<MapState>()(
  devtools(
    subscribeWithSelector((_set) => defaultState),
    { name: 'MapState' }
  )
)
